import type { ReactNode } from 'react';
import { Card, CardContent, MetricInfoPopover } from '@/components/ui';
import { PanelShowAllToggle } from './PanelShowAllToggle';

interface RankedPanelShellProps {
  testId: string;
  title: string;
  countLabel: string;
  metricKey: string;
  totalRows: number;
  defaultLimit: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  emptyState: string | null;
  children: ReactNode;
}

/**
 * Shared chrome for the three Code Quality distribution panels (Failing QG /
 * Lowest coverage / Worst reliability). Owns the header, count chip, info
 * popover, body slot, and the "Show all (N)" footer toggle. Extracted to keep
 * the three panel components small enough to avoid SonarCloud duplication on
 * new code (PRODOPSAI-333).
 */
export function RankedPanelShell({
  testId,
  title,
  countLabel,
  metricKey,
  totalRows,
  defaultLimit,
  expanded,
  onToggleExpanded,
  emptyState,
  children,
}: Readonly<RankedPanelShellProps>) {
  const showFooter = emptyState === null && totalRows > defaultLimit;

  return (
    <Card className="shadow-none" data-testid={testId}>
      <CardContent className="p-4">
        <header className="flex items-baseline justify-between">
          <h3 className="flex items-center text-sm font-semibold text-foreground">
            <span>{title}</span>
            <span className="ml-2 text-xs font-normal text-muted-foreground">{countLabel}</span>
            <MetricInfoPopover metricKey={metricKey} />
          </h3>
        </header>
        {emptyState === null ? (
          <ul className="mt-3 space-y-1" data-testid={`${testId}-list`}>
            {children}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground">{emptyState}</p>
        )}
        {showFooter && (
          <PanelShowAllToggle
            expanded={expanded}
            totalRows={totalRows}
            defaultLimit={defaultLimit}
            onToggle={onToggleExpanded}
          />
        )}
      </CardContent>
    </Card>
  );
}
